import React, { useEffect, useRef } from 'react';
import {
  Bold,
  Eraser,
  Italic,
  Link,
  List,
  ListOrdered,
  Underline,
} from 'lucide-react';
import { sanitizeRichTextHtml } from '../lib/richText';

const toolbarItems = [
  { command: 'bold', label: 'Bold', Icon: Bold },
  { command: 'italic', label: 'Italic', Icon: Italic },
  { command: 'underline', label: 'Underline', Icon: Underline },
  { command: 'insertUnorderedList', label: 'Bulleted list', Icon: List },
  { command: 'insertOrderedList', label: 'Numbered list', Icon: ListOrdered },
  { command: 'removeFormat', label: 'Clear formatting', Icon: Eraser },
];

const RichTextEditor = ({ value, onChange, minHeight = '10rem', testId }) => {
  const editorRef = useRef(null);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || document.activeElement === editor) return;

    const sanitized = sanitizeRichTextHtml(value);
    if (editor.innerHTML !== sanitized) {
      editor.innerHTML = sanitized;
    }
  }, [value]);

  const emitChange = () => {
    const editor = editorRef.current;
    if (!editor) return;
    onChange(sanitizeRichTextHtml(editor.innerHTML));
  };

  const runCommand = (command, commandValue = null) => {
    const editor = editorRef.current;
    if (!editor) return;

    editor.focus();
    document.execCommand(command, false, commandValue);
    emitChange();
  };

  const addLink = () => {
    const href = window.prompt('Paste link URL');
    if (!href) return;
    runCommand('createLink', href);
  };

  const handlePaste = (event) => {
    const html = event.clipboardData.getData('text/html');
    const text = event.clipboardData.getData('text/plain');
    const pastedContent = html || text;

    if (!pastedContent) return;

    event.preventDefault();
    document.execCommand('insertHTML', false, sanitizeRichTextHtml(pastedContent));
    emitChange();
  };

  const handleBlur = () => {
    const editor = editorRef.current;
    if (!editor) return;

    const sanitized = sanitizeRichTextHtml(editor.innerHTML);
    if (editor.innerHTML !== sanitized) {
      editor.innerHTML = sanitized;
    }
    onChange(sanitized);
  };

  return (
    <div className="overflow-hidden rounded-lg border border-gray-300 bg-white focus-within:ring-2 focus-within:ring-red-500">
      <div className="flex flex-wrap gap-1 border-b border-gray-200 bg-gray-50 px-2 py-2">
        {toolbarItems.map(({ command, label, Icon }) => (
          <button
            key={command}
            type="button"
            title={label}
            aria-label={label}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => runCommand(command)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-gray-700 transition hover:bg-white hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            <Icon size={17} />
          </button>
        ))}
        <button
          type="button"
          title="Add link"
          aria-label="Add link"
          onMouseDown={(event) => event.preventDefault()}
          onClick={addLink}
          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-gray-700 transition hover:bg-white hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
        >
          <Link size={17} />
        </button>
      </div>
      <div
        ref={editorRef}
        data-testid={testId}
        role="textbox"
        aria-multiline="true"
        contentEditable
        suppressContentEditableWarning
        onInput={emitChange}
        onPaste={handlePaste}
        onBlur={handleBlur}
        className="rich-text-content w-full overflow-y-auto px-4 py-3 text-sm leading-6 text-gray-800 outline-none empty:before:text-gray-400 empty:before:content-[attr(data-placeholder)]"
        data-placeholder="Write the achievement details..."
        style={{ minHeight }}
      />
    </div>
  );
};

export default RichTextEditor;
